import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';

// Axios client with auth and error handling

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor
 * Adds Firebase Auth token to all requests
 */
apiClient.interceptors.request.use(
  (config) => {
    // Get auth token from localStorage (or your auth state management)
    const token = localStorage.getItem('authToken');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * Transforms backend error responses to match Electron IPC error format
 */
apiClient.interceptors.response.use(
  (response) => {
    // Unwrap the response data
    return response.data;
  },
  (error: AxiosError<any>) => {
    // Handle network errors
    if (!error.response) {
      const networkError = `<zaError>${JSON.stringify({
        name: 'network-error',
        message: 'Network error: Unable to reach server',
        title: 'Connection Error',
      })}</zaError>`;
      return Promise.reject(new Error(networkError));
    }

    // Transform HTTP error responses to <zaError> format
    if (error.response?.data?.error) {
      const appError = error.response.data.error;
      const zaError = `<zaError>${JSON.stringify({
        name: appError.code,
        message: appError.message,
        title: appError.title || appError.code,
        debugMessage: appError.debugMessage,
      })}</zaError>`;
      return Promise.reject(new Error(zaError));
    }

    // Fallback for unexpected errors
    const fallbackError = `<zaError>${JSON.stringify({
      name: 'unknown-error',
      message: error.message || 'An unexpected error occurred',
      title: 'Error',
    })}</zaError>`;
    return Promise.reject(new Error(fallbackError));
  }
);

export default apiClient;
